/**
 * Provider Agreement API
 *
 * Handles publisher agreement acceptance status via the Organization API.
 * Agreement content is served from static files (src/content/legal/).
 *
 * @see GW-339 Provider Agreement acceptance modal
 * @see GW-502 Publisher agreement persistence fix
 * @see GW-525 Add dynamic form fields for Provider Agreement acceptance
 */
import { organizationsApi } from '@/api/organizations';
import { getConfig } from '@/config/runtimeConfig';
import { getAuthToken } from '@/lib/devStackAuth.util';
// GW-6533: Validate orgId at API client boundary so frontend regressions
// throw a distinct InvalidOrgIdError before the network call leaves the
// browser, instead of producing a noisy 4xx after a round-trip.
import { assertOrgId } from '@/types/orgId';

/**
 * A single historical acceptance record for a publisher agreement
 */
export interface AgreementRecord {
  /** Version of the agreement that was accepted */
  agreementVersion: string;
  /** ISO timestamp when the agreement was accepted */
  acceptedAt: string;
  /** User ID who accepted the agreement */
  acceptedByUserId: string;
  /** Provider/organization name at time of acceptance (optional) */
  providerName?: string;
  /** GW-8525: Street address (address1 + address2) captured at acceptance time */
  providerAddress?: string;
  /** GW-8525: City captured at acceptance time */
  city?: string;
  /** GW-8525: State captured at acceptance time */
  state?: string;
  /** GW-8525: Postal code captured at acceptance time */
  postalCode?: string;
}

/**
 * Status of publisher agreement acceptance for an organization
 */
export interface PublisherAgreementStatus {
  /** Whether the organization has accepted the current version */
  hasAcceptedCurrent: boolean;
  /** Current version of the publisher agreement */
  currentVersion: string;
  /** Version the organization accepted (null if never accepted) */
  acceptedVersion: string | null;
  /** When the agreement was accepted (ISO string, null if never) */
  acceptedAt: string | null;
  /** User ID who accepted the agreement (null if never) */
  acceptedBy: string | null;
  /** Full history of all acceptance records, newest first */
  agreements: AgreementRecord[];
}

/**
 * Organization details required for agreement acceptance
 * @see GW-525 Add dynamic form fields for Provider Agreement acceptance
 */
export interface AcceptAgreementOrganizationDetails {
  /** Provider/organization name for the agreement */
  organization_name: string;
  /** Street address line 1 */
  address1: string;
  /** Street address line 2 (optional) */
  address2?: string;
  /** City */
  city: string;
  /** State/Province */
  state: string;
  /** Postal/ZIP code */
  postal_code: string;
}

/**
 * Result of accepting the publisher agreement
 */
interface AcceptPublisherAgreementResult {
  success: boolean;
  acceptedAt: string;
}

class PublisherAgreementApiClient {
  /**
   * Get the publisher agreement acceptance status for an organization
   *
   * Calls the dedicated status endpoint to get the org's acceptance state,
   * including the version they accepted and whether re-acceptance is needed.
   *
   * @param orgId - Organization ID to check
   * @returns Acceptance status for the organization
   *
   * @see GW-2349 Fix publisher agreement re-acceptance logic
   */
  async getPublisherAgreementStatus(orgId: string): Promise<PublisherAgreementStatus> {
    if (!orgId) {
      throw new Error('Organization ID required');
    }
    assertOrgId(orgId);

    const baseURL = getConfig().apiBaseUrl;
    const token = await getAuthToken();

    const response = await fetch(`${baseURL}/organizations/${orgId}/agreements/publisher/status`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to fetch agreement status: ${response.status}`);
    }

    const data = await response.json();

    return {
      hasAcceptedCurrent: data.hasPublisherAgreed ?? false,
      currentVersion: '',
      acceptedVersion: data.agreements?.[0]?.agreementVersion ?? null,
      acceptedAt: data.agreements?.[0]?.acceptedAt ?? null,
      acceptedBy: data.agreements?.[0]?.acceptedByUserId ?? null,
      agreements: (data.agreements ?? []).map(
        (a: {
          agreementVersion: string;
          acceptedAt: string;
          acceptedByUserId: string;
          providerName?: string;
          providerAddress?: string;
          city?: string;
          state?: string;
          postalCode?: string;
        }) => ({
          agreementVersion: a.agreementVersion,
          acceptedAt: a.acceptedAt,
          acceptedByUserId: a.acceptedByUserId,
          providerName: a.providerName,
          // GW-8525: Surface the signed address so view-only mode can render
          // the actual accepted details instead of falling back to placeholders.
          providerAddress: a.providerAddress,
          city: a.city,
          state: a.state,
          postalCode: a.postalCode,
        })
      ),
    };
  }

  /**
   * Record organization's acceptance of the publisher agreement
   *
   * Calls the dedicated agreement accept endpoint with organization details.
   * The backend (GW-527) stores these details for the agreement template placeholders.
   *
   * @param orgId - Organization ID accepting the agreement
   * @param version - Version being accepted
   * @param organizationDetails - Organization details for the agreement (GW-525)
   * @returns Result with success status and timestamp
   * @see GW-525 Add dynamic form fields for Provider Agreement acceptance
   * @see GW-527 Backend Lambda handler for organization details
   */
  async acceptPublisherAgreement(
    orgId: string,
    version: string,
    organizationDetails?: AcceptAgreementOrganizationDetails
  ): Promise<AcceptPublisherAgreementResult> {
    // Validate inputs
    if (!orgId) {
      throw new Error('Organization ID required');
    }
    assertOrgId(orgId);
    if (!version) {
      throw new Error('Invalid version');
    }

    // Validate organization details if provided (GW-525)
    if (organizationDetails) {
      if (!organizationDetails.organization_name) {
        throw new Error('Organization name is required');
      }
      if (!organizationDetails.address1) {
        throw new Error('Address line 1 is required');
      }
      if (!organizationDetails.city) {
        throw new Error('City is required');
      }
      if (!organizationDetails.state) {
        throw new Error('State is required');
      }
      if (!organizationDetails.postal_code) {
        throw new Error('Postal code is required');
      }
    }

    // If organization details are provided, use the dedicated accept endpoint (GW-525/GW-527)
    if (organizationDetails) {
      const baseURL = getConfig().apiBaseUrl;
      const token = await getAuthToken();

      const response = await fetch(
        `${baseURL}/organizations/${orgId}/agreements/publisher/accept`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({
            version,
            ...organizationDetails,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to accept agreement: ${response.status}`);
      }

      const result = await response.json();
      return {
        success: result.success ?? true,
        acceptedAt: result.acceptedAt ?? new Date().toISOString(),
      };
    }

    // Fallback for backwards compatibility (without organization details)
    // Note: This updates stripeConnectedAt as a proxy for agreement acceptance
    // In production, the proper flow is through the /agreements/publisher/accept endpoint
    const acceptedAt = new Date().toISOString();
    await organizationsApi.updateOrganization(orgId, {
      stripeConnectedAt: acceptedAt,
    });

    return {
      success: true,
      acceptedAt,
    };
  }
}

// Singleton instance
export const publisherAgreementApi = new PublisherAgreementApiClient();
